/* Floating banner shown on rep-portal pages when the owner is in
   "Preview as demo rep" mode. Reads the owner-token snapshot stashed by
   RepLogin's `previewAsDemoRep` handler and offers a one-tap return. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftCircle, X } from "lucide-react";
import api, { setAccessToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SNAPSHOT_KEY = "ld_owner_preview_snapshot";

/** Called from RepLogin BEFORE the session is swapped to the demo rep.
 *  Grabs the current owner's token (from localStorage — set by AuthContext
 *  on login) and remembers their email so the banner can render properly. */
export function stashOwnerSnapshot(ownerToken, ownerEmail) {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
      token: ownerToken, email: ownerEmail,
      stashed_at: new Date().toISOString(),
    }));
  } catch { /* ignore */ }
}

export function clearOwnerSnapshot() {
  try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch { /* ignore */ }
}

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function OwnerPreviewBanner() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [snap, setSnap] = useState(readSnapshot);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Refresh snapshot on user change (e.g. after switching sessions)
  useEffect(() => { setSnap(readSnapshot()); }, [user?.id]);

  // Only show when: snapshot exists, current user is a rep, not dismissed
  const active = snap && !dismissed && user && user.role === "rep";
  if (!active) return null;

  const returnToOwner = async () => {
    setBusy(true);
    try {
      // Clear the rep session cookie first, then restore the owner token.
      try { await api.post("/auth/logout"); } catch { /* ignore */ }
      const { data } = await api.post("/auth/restore-session",
                                        { access_token: snap.token });
      if (data.access_token) setAccessToken(data.access_token);
      await refresh();
      clearOwnerSnapshot();
      toast.success(`Welcome back, ${snap.email}`);
      nav("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("expired") || detail.includes("Invalid")) {
        clearOwnerSnapshot();
        toast.error("Owner session expired — sign in again.");
        nav("/login");
      } else {
        toast.error(detail || "Couldn't restore your owner session.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm shadow-2xl"
         data-testid="owner-preview-banner">
      <div className="rounded-xl border border-amber-500/40 bg-neutral-900/95 backdrop-blur px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
          <ArrowLeftCircle className="text-amber-300" size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
            Preview mode
          </div>
          <div className="text-xs text-neutral-300 truncate">
            Signed in as demo rep. Return to <span className="font-mono text-neutral-100">{snap.email}</span>?
          </div>
        </div>
        <button onClick={returnToOwner} disabled={busy}
                className="rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-950 text-xs font-bold px-3 py-1.5 flex-shrink-0 disabled:opacity-60"
                data-testid="owner-preview-return">
          {busy ? "…" : "Return"}
        </button>
        <button onClick={() => setDismissed(true)}
                className="text-neutral-500 hover:text-neutral-200 flex-shrink-0"
                title="Dismiss banner (session stays as demo rep)"
                data-testid="owner-preview-dismiss">
          <X size={14}/>
        </button>
      </div>
    </div>
  );
}
